import type { ToolId } from "./tools";

export interface ToolSeo {
  title: string;
  description: string;
  heading: string;
  intro: string[];
  uses: string[];
  faqs: { q: string; a: string }[];
}

const FAIR = "DITHER uses your browser's cryptographically secure random number generator (crypto.getRandomValues), so every result is independent and unbiased — nothing is pre-decided or stored before you tap.";

export const TOOL_SEO: Record<ToolId, ToolSeo> = {
  "heads-or-tails": {
    title: "Flip a Coin Online – Heads or Tails in 1 Second | DITHER",
    description: "Free online coin flip. Tap once for a fair 50/50 heads or tails result with a 3D coin toss, then save or share the verdict.",
    heading: "Flip a coin online: fair heads or tails, instantly",
    intro: ["No coin in your pocket? DITHER's virtual coin toss gives a true 50/50 heads or tails result in about a second, with a spinning gold coin and a big, screenshot-worthy reveal.", FAIR],
    uses: ["Settle who goes first in a game", "Break a two-way tie between friends", "Decide between two dinner plans", "Pick a side for a debate"],
    faqs: [
      { q: "Is an online coin flip really 50/50?", a: "Yes. Each flip uses a secure random number with exactly two equally likely outcomes, so heads and tails each have a 50% chance every time." },
      { q: "Can I flip the coin again?", a: "Tap Again for an instant re-flip. Every flip is independent — previous results never affect the next one." },
      { q: "Can I share the result?", a: "Yes. Tap Share to send a branded DITHER result card to your group chat, or save it to your favourites." },
    ],
  },
  "roll-the-dice": {
    title: "Roll a Dice Online – Free Virtual Dice Roller (1–3 Dice) | DITHER",
    description: "Roll one, two or three virtual dice online. Fair six-sided dice roller with animated rolls and instant totals — perfect for board games.",
    heading: "Online dice roller: one, two or three dice",
    intro: ["Lost the dice for board game night? Roll up to three fair six-sided dice and DITHER adds up the total for you.", FAIR],
    uses: ["Board games and tabletop RPG quick rolls", "Drinking-free party games", "Choosing a number from 1–6 (or 3–18)", "Classroom probability lessons"],
    faqs: [
      { q: "How many dice can I roll?", a: "Choose 1, 2 or 3 standard six-sided dice. With multiple dice DITHER shows each face and the total." },
      { q: "Is the virtual dice fair?", a: "Every die face from 1 to 6 has an equal one-in-six chance, generated with a secure random number source." },
      { q: "Does it work on my phone?", a: "Yes — DITHER is a mobile-first web app that works on iPhone, Android, tablet and desktop with no download." },
    ],
  },
  "red-or-black": {
    title: "Red or Black – Random Card Draw Game Online | DITHER",
    description: "Draw a random playing card from a 52-card deck. Red (hearts, diamonds) or black (spades, clubs)? A fast, fair card-flip decider.",
    heading: "Red or black: draw a random card",
    intro: ["DITHER shuffles a virtual 52-card deck and flips one card. Hearts and diamonds are red, spades and clubs are black — a classic party game and a handy 50/50 decider.", FAIR],
    uses: ["The classic red-or-black party game", "A card-flavoured coin toss", "Picking teams", "Quick random card for magic tricks"],
    faqs: [
      { q: "What are the odds of red or black?", a: "Exactly 50/50: 26 red cards and 26 black cards, and every one of the 52 cards is equally likely." },
      { q: "Does it show the actual card?", a: "Yes, DITHER flips a full playing card so you see the rank and suit as well as the colour." },
    ],
  },
  "odd-or-even": {
    title: "Odd or Even Generator – Random Number Decider 1–100 | DITHER",
    description: "Let a random number from 1 to 100 decide: odd or even? Quick, fair and fun for games, bets and settling small arguments.",
    heading: "Odd or even? Let a random number decide",
    intro: ["DITHER rolls a number from 1 to 100 and tells you whether it's odd or even — a fair alternative to a coin flip when you want to see the number too.", FAIR],
    uses: ["Odds-and-evens hand games", "Deciding turns", "A 50/50 pick with a visible number", "Classroom maths warm-ups"],
    faqs: [
      { q: "Are odd and even equally likely?", a: "Yes. Between 1 and 100 there are exactly 50 odd and 50 even numbers, each equally likely." },
      { q: "Can I see the number that was rolled?", a: "Yes, the number appears under the result so everyone can check." },
    ],
  },
  "this-or-that": {
    title: "This or That Picker – Choose Between Two Options | DITHER",
    description: "Can't choose between two things? Type both options and DITHER picks one fairly in a second. Perfect for 'this or that' decisions.",
    heading: "This or that: pick between two options",
    intro: ["Pizza or tacos? Stay in or go out? Type your two options and DITHER spins a slot reel and lands on one — no overthinking required.", FAIR],
    uses: ["Two-way food and film choices", "Outfit dilemmas", "Weekend plans", "Would-you-rather games"],
    faqs: [
      { q: "Can I use any two options?", a: "Yes, type anything up to 80 characters for each option. They just need to be different." },
      { q: "What if I don't like the answer?", a: "Hit Again for an instant re-pick. Many people find their gut reaction to the result tells them what they really wanted." },
      { q: "Can I choose between more than two things?", a: "Yes — use a category, a custom wheel or a group vote for three or more options." },
    ],
  },
  "raffle-draw": {
    title: "Raffle Draw Generator – Pick Winning Ticket Numbers Online | DITHER",
    description: "Free online raffle draw: enter how many tickets you sold, set up to 5 prizes and DITHER draws the winning ticket numbers from 1st to 5th place.",
    heading: "Raffle draw: pick your winning tickets",
    intro: ["Enter the number of tickets sold, choose how many prizes you're giving away and name them — or keep the default virtual gifts. DITHER draws a different winning ticket for every prize and reveals them from last place up to the 1st prize winner.", FAIR],
    uses: ["School and charity raffles", "Office sweepstakes and tombolas", "Fundraiser prize draws", "Giveaways and competitions"],
    faqs: [
      { q: "Can the same ticket win twice?", a: "No. Every prize goes to a different ticket number." },
      { q: "How many prizes can I draw?", a: "Between 1 and 5 prizes per draw, and you can rename each one, for example \"£50 voucher\"." },
      { q: "How are tickets numbered?", a: "Tickets run from 1 up to the number of tickets you enter, and every ticket has an equal chance." },
    ],
  },
  "higher-or-lower": {
    title: "Higher or Lower Game Online – Guess the Next Number | DITHER",
    description: "Play higher or lower online: guess whether the next random number (1–100) is higher or lower and build your best streak.",
    heading: "Higher or lower: the number guessing game",
    intro: ["DITHER shows a number from 1 to 100. Guess whether the next one will be higher or lower, keep going, and try to beat your best streak.", FAIR],
    uses: ["Quick solo game when bored", "Party game with forfeits", "Teaching probability", "Settling who buys the next round"],
    faqs: [
      { q: "How does scoring work?", a: "Each correct guess adds one to your streak. A wrong guess resets the streak, and your best streak is shown alongside." },
      { q: "Is there a strategy?", a: "Yes — if the current number is above 50, 'lower' is statistically more likely, and vice versa." },
    ],
  },
};
